import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  UserPlus,
  X,
  CheckCircle2,
  Users,
  ShieldCheck,
  Loader2,
  Trash2,
  Edit2,
  Mail,
  RefreshCcw,
  Image as ImageIcon,
  ShieldAlert,
  AlertCircle, // Fixed: Added this back for the error display
} from "lucide-react";
import Skeleton from "../components/Skeleton";
import "../styles/Roster.css";

const Roster = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    role: "",
    email: "",
    img: "",
  });

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("staff")
      .select("*")
      .order("created_at", { ascending: true });
    if (!error) setAgents(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage(""); // Reset error message on new attempt

    const finalImg =
      formData.img ||
      `https://ui-avatars.com/api/?name=${formData.name}&background=random&color=fff`;
    
    const { error } = await supabase
      .from("staff")
      .insert([{ ...formData, img: finalImg }]);

    if (!error) {
      setIsModalOpen(false);
      setFormData({ name: "", role: "", email: "", img: "" });
      await fetchAgents();
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } else {
      setErrorMessage(error.message); // Fixed: This now updates state
    }
    setSubmitting(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { error } = await supabase
      .from("staff")
      .delete()
      .eq("id", deleteTarget.id);
    if (!error) {
      setDeleteTarget(null);
      fetchAgents();
    }
  };

  const filteredAgents = agents.filter(
    (a) =>
      (a.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.role || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="roster-page">
      {/* ... previous header and table code remains same ... */}
      <header className="roster-header">
        <div className="header-info">
          <h1>Staff Directory</h1>
          <p>Total Personnel: {agents.length}</p>
        </div>
        <div className="header-actions">
          <button className="refresh-btn" onClick={fetchAgents}>
            <RefreshCcw size={18} className={loading ? "spinner" : ""} />
          </button>
          <button
            className="add-agent-btn"
            onClick={() => {
              setErrorMessage(""); // Clear old errors when opening
              setIsModalOpen(true);
            }}
          >
            <UserPlus size={19} /> Add Member
          </button>
        </div>
      </header>

      <div className="table-actions">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search records..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="roster-container">
        <div className="table-wrapper">
          <table className="agent-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Identity & Contact</th>
                <th>Designation</th>
                <th className="text-right">Manage</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td><Skeleton type="s-rank" /></td>
                      <td className="name-cell">
                        <Skeleton type="s-avatar" />
                        <div className="name-stack">
                          <Skeleton type="s-name" />
                          <Skeleton type="s-email" />
                        </div>
                      </td>
                      <td><Skeleton type="s-role" /></td>
                      <td className="text-right"><Skeleton type="s-rank" /></td>
                    </tr>
                  ))
                : filteredAgents.map((agent, index) => (
                    <tr key={agent.id}>
                      <td><span className="number-badge">{index + 1}</span></td>
                      <td className="name-cell">
                        <img src={agent.img} alt="" className="agent-avatar-img" />
                        <div className="name-stack">
                          <span className="main-name">{agent.name}</span>
                          <span className="sub-email">{agent.email}</span>
                        </div>
                      </td>
                      <td><span className="role-text">{agent.role}</span></td>
                      <td className="text-right">
                        <div className="action-group">
                          <button className="action-icon edit"><Edit2 size={15} /></button>
                          <button className="action-icon delete" onClick={() => setDeleteTarget(agent)}>
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal with Error Message Display */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="modal-overlay">
            <motion.div
              className="modal-box preview-layout"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              <button className="custom-close-btn" onClick={() => setIsModalOpen(false)}>
                <X size={20} />
              </button>

              <div className="modal-content-grid">
                <div className="preview-section">
                  <h3>Live Preview</h3>
                  <div className="preview-card">
                    <img
                      src={formData.img || `https://ui-avatars.com/api/?name=${formData.name || "User"}&background=random`}
                      alt="Preview"
                    />
                    <div className="preview-info">
                      <h4>{formData.name || "Member Name"}</h4>
                      <p>{formData.role || "Designation"}</p>
                      <small>{formData.email || "email@example.com"}</small>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="admin-form">
                  <div className="modal-header-text">
                    <h2>New Member</h2>
                    <p>Fill out the profile details.</p>
                  </div>

                  {/* Error Banner - This uses the errorMessage variable */}
                  {errorMessage && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      className="error-banner"
                    >
                      <AlertCircle size={16} />
                      <span>{errorMessage}</span>
                    </motion.div>
                  )}

                  <div className="form-group">
                    <label>Full Name</label>
                    <div className="input-with-icon">
                      <Users size={17} className="field-icon" />
                      <input required placeholder="Name" value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Designation</label>
                    <div className="input-with-icon">
                      <ShieldCheck size={17} className="field-icon" />
                      <input required placeholder="e.g. Underwriter" value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <div className="input-with-icon">
                      <Mail size={17} className="field-icon" />
                      <input required type="email" placeholder="Email" value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Profile URL (Optional)</label>
                    <div className="input-with-icon">
                      <ImageIcon size={17} className="field-icon" />
                      <input placeholder="https://..." value={formData.img}
                        onChange={(e) => setFormData({ ...formData, img: e.target.value })}
                      />
                    </div>
                  </div>

                  <button type="submit" className="submit-btn" disabled={submitting}>
                    {submitting ? <Loader2 className="spinner" size={18} /> : "Finalize & Save"}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation */}
      <AnimatePresence>
        {deleteTarget && (
          <div className="modal-overlay">
            <motion.div className="delete-confirm-box" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
              <div className="alert-icon-wrap"><ShieldAlert size={32} /></div>
              <h2>Confirm Deletion</h2>
              <p>Are you sure you want to remove <strong>{deleteTarget.name}</strong>?</p>
              <div className="delete-actions">
                <button className="cancel-delete" onClick={() => setDeleteTarget(null)}>Keep Agent</button>
                <button className="confirm-delete-btn" onClick={confirmDelete}>Delete Record</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <AnimatePresence>
        {showToast && (
          <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="success-toast-green">
            <CheckCircle2 size={20} /> <span>Directory Updated</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Roster;